// NotesApp source file: home.component.ts

import { Component, OnInit, OnDestroy, AfterViewInit, ViewChild, HostListener } from '@angular/core';
import { ElementRef, ChangeDetectorRef, ViewContainerRef, computed, inject } from '@angular/core';
import { RouterModule, Router, ActivatedRoute } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { filter, take, switchMap, takeUntil, catchError, distinctUntilChanged } from 'rxjs/operators';
import { Observable, Subscription, Subject, of, firstValueFrom } from 'rxjs';

// PRIMENG GUI COMPONENTS
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { StyleClassModule } from 'primeng/styleclass';
import { Menu } from 'primeng/menu';
import { MenuItem, MessageService } from 'primeng/api';
import { MenuModule } from 'primeng/menu';
import { TieredMenuModule } from 'primeng/tieredmenu';
import { InputTextModule } from 'primeng/inputtext';
import { BlockUIModule } from 'primeng/blockui';
import { DialogModule } from 'primeng/dialog';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SplitButtonModule } from 'primeng/splitbutton';
import { SliderModule } from 'primeng/slider';
import { SplitterModule } from 'primeng/splitter';
import { PopoverModule } from 'primeng/popover';

// Notes-App Services
import { ApiResponse, Note, Project, Status, NoteType, DataService, Category, Tag } from '../../_service/data.service';
import { BulkCategoryResult, BulkStatusResult, BulkDeleteResult, BulkTagAddResult, BulkTagRemoveResult } from '../../_service/data.service';
import { FilterParams, FilterService } from '../../_service/filter.service';
import { AuthService } from '../../_service/auth.service';
import { LayoutService } from '../../_service/layout.service';
//import { OLD__UserSettingsService } from '../../_service/OLD_usersettings.service';
import { EnvironmentService } from '../../_service/sync/environment.service';
import { AlertService } from '../../_service/alert.service';
import { LogService } from '../../_service/log.service';
import { SyncCoordinatorService } from '../../_service/sync/sync-coordinator.service';
import { RemoteChangesService } from '../../_service/sync/remote-changes.service';
import { AppVersionInfo, AppVersionService } from '../../_service/app-version.service';
import { AppUpdateService } from '../../_service/app-update.service';

import { AlertComponent } from '../alert/alert.component';
import { DialogType, GlobalDialogService } from '../../_service/global-dialog.service';
import { GlobalDialogComponent } from '../global-dialog/global-dialog.component';
import Delta from 'quill-delta';

import { BulkActionConfig, BulkActionDialogComponent, BulkActionRequest, ExportFormat } from './bulk-action-dialog/bulk-action-dialog.component';
import { HeaderComponent } from './header/header.component';
import { ProjectCardsComponent } from './project-cards/project-cards.component';
import { CardsComponent } from './cards/cards.component';
import { CategoryCardsComponent } from './category-cards/category-cards.component';
import { TagCardsComponent } from './tag-cards/tag-cards.component';
import { ListComponent, LIST_SORT_STATE_KEY, LIST_SORT_STATE_DEFAULT } from './list/list.component';
import { CalendarViewComponent } from './calendar/calendar.component';
import { QuickAccessComponent } from './quick-access/quick-access.component';
import { UserSettingsService } from '../../_service/user-settings.service';
import { NotePrintExportService } from '../../_service/note-print-export.service';
import { AiSessionService } from '../../_service/ai-session.service';
import { SemanticSearchResult, SemanticSearchService } from '../../_service/semantic-search.service';
import { FilterLogic } from './logic/filter.logic';
import { SortingLogic } from './logic/sorting.logic';
import { BulkActionsLogic, ExportSortOrder } from './logic/bulk-actions.logic';
import { SyncStateLogic, SyncStatus, SyncState, ConnectivityDisplay, resolveConnectivityDisplay } from './logic/sync-state.logic';
import { TemplateLogic } from './logic/template.logic';
import { MenuStateLogic } from './logic/menu-state.logic';
import { NavigationLogic } from './logic/navigation.logic';
import { UIStateLogic } from './logic/ui-state.logic';

type CardSort = {
  project_id: number;
  sort_order: number;
};

type HomeUiState = {
  view?: string;
  calendarMonth?: string;
  calendarVisibility?: 'calendar' | 'all';
};

type HumanReadableFilterItem = {
  label: string;
  value: string;
};

const HOME_UI_STATE_STORAGE_KEY = 'notesapp.home.uiState.v1';

@Component({
  selector: 'app-home',
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    RouterModule,
    MenuModule,
    TieredMenuModule,
    InputTextModule,
    StyleClassModule,
    SelectModule,
    ButtonModule,
    AlertComponent,
    BlockUIModule,
    DialogModule,
    ProgressSpinnerModule,
    SplitButtonModule,
    SliderModule,
    SplitterModule,
    PopoverModule,
    GlobalDialogComponent,
    BulkActionDialogComponent,
    HeaderComponent,
    ProjectCardsComponent,
    CardsComponent,
    CategoryCardsComponent,
    TagCardsComponent,
    ListComponent,
    CalendarViewComponent,
    QuickAccessComponent,
  ],
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
})
export class HomeComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('pdfContent', { read: ViewContainerRef, static: true }) pdfContent!: ViewContainerRef;
  @ViewChild('MobileSearchField') MobileSearchField!: ElementRef<HTMLInputElement>;
  @ViewChild('DesktopSearchField') DesktopSearchField!: ElementRef<HTMLInputElement>;
  @ViewChild('menu') menu!: Menu;
  @ViewChild('userMenu') userMenu!: Menu;
  @ViewChild('syncLogContainer') syncLogContainer?: ElementRef<HTMLDivElement>;
  @ViewChild('zoomTrigger', { read: ElementRef }) zoomTriggerRef?: ElementRef<HTMLElement>;
  @ViewChild('zoomPanel', { read: ElementRef }) zoomPanelRef?: ElementRef<HTMLElement>;

  // Inject AI session service for opening dialog from menu
  private readonly aiSessionService = inject(AiSessionService);

  @HostListener('document:keydown.escape') onEsc() {
    if (this.showZoomPanel) {
      this.showZoomPanel = false;
    }
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.isQaSmallScreen = typeof window !== 'undefined' && window.innerWidth < 640;
  }

  @HostListener('document:click', ['$event'])
  onDocClick(ev: MouseEvent) {
    const path = (ev.composedPath?.() || []) as EventTarget[];
    const clickedTrigger = this.zoomTriggerRef?.nativeElement ? path.includes(this.zoomTriggerRef.nativeElement) : false;
    const clickedInsidePanel = this.zoomPanelRef?.nativeElement ? path.includes(this.zoomPanelRef.nativeElement) : false;
    
    if (!clickedTrigger && !clickedInsidePanel && this.showZoomPanel) {
      this.showZoomPanel = false;
    }
  }

  allNotes: Note[] = []; // renamed from "notes" 18.Oct.25
  filteredNotes: Note[] = [];

  // Quick-access side panel state
  quickAccessCollapsed = false;
  quickAccessSizes: number[] = [18, 82];
  isQaSmallScreen = false;
  private readonly qaCollapsedKey = 'quick-access-panel-collapsed';
  private readonly qaSizesKey = 'quick-access-panel-sizes';
  private lastQuickAccessSizes: number[] = [18, 82];
  availableTags: Tag[] = [];
  sortBy: string = '';
  sortOrder: 'asc' | 'desc' = 'asc';
  filter: FilterParams = {};
  projects: Project[] = [];
  card_sort: CardSort[] = [];
  statusValues: string[] = [];
  types: string[] = [];
  isHeaderVisible: boolean = true;
  isWorkToggle: boolean = false; // Initialize based on your requirement
  //selectedProjectId: number | null = null; // selected project ID for export PDF ??
  loadingCounter = 0; // initially count of loading objects in progress
  isOnline: boolean = true; // Tracks online/offline status from EnvironmentService
  // Whether the server actually responded to the last sync attempt — distinct from
  // isOnline, which only reflects the browser's network interface. See SyncStateLogic.
  serverReachable: boolean = true;
  get connectivity(): ConnectivityDisplay {
    return resolveConnectivityDisplay(this.isOnline, this.serverReachable);
  }
  MobileFilterMenuIsVisible: boolean = false;
  MobileAppMenuIsVisible: boolean = false;
  categoryItems: { ID: string; ItemName: string; isSelected?: Boolean }[] = [];
  selectedCategoryIDs: string[] = [];
  selectedStatus: string = '';
  selectedView: string = 'List';
  calendarMonthState: string | null = null; // yyyy-mm for Calendar view
  calendarVisibilityState: 'calendar' | 'all' = 'calendar';
  templateDialogVisible = false;
  templates: Note[] = [];
  templatesLoading = false;
  templateLoadError = '';
  templateMenuItems: MenuItem[] = [];
  homeZoomScale = 1;
  systemZoomFactor = 100;
  showZoomPanel = false;
  zoomPanelStyle: any = {};
  // Dialog state
  private _showSyncDialog = false;
  isSyncing = false;
  isSelectMode: boolean = false;
  selectedNoteIds: string[] = []; // notes selected in select mode    ?? Unclear if this is needed here!!!!
  private previousViewBeforeSelectMode: string | null = null;
  bulkActions: BulkActionConfig[] = [];
  bulkActionMenuItems: MenuItem[] = [];
  bulkDialogVisible = false;
  currentBulkAction: BulkActionConfig | null = null;
  favoriteDialogVisible = false;
  favoriteDialogFavorite = true;
  favoriteDialogUpdateTimestamp = true;
  // Status + messages
  syncStatus: SyncStatus = {
    couchdb: 'offline',
    indexeddb: 'ok',
    lastSynced: null,
  };

  messageLog: string[] = [];
  categories: Category[] = [];
  selectedCategory: Category | null = null;

  private sub?: Subscription;

  layoutService: LayoutService = inject(LayoutService);
  selectedProject: Project | undefined;
  isDarkMode = computed(() => this.layoutService.appState().darkMode);
  pageMenuItems: MenuItem[] = [];
  filterMenuItems: MenuItem[] = [];
  userMenuItems: MenuItem[] = [];
  globalStatusMessage: string = 'Please wait...';
  semanticResults: SemanticSearchResult[] = [];
  semanticSearchLoading = false;
  semanticSearchError = '';
  filterInfoDialogVisible = false;
  filterInfoJsonDialogVisible = false;
  appInfoDialogVisible = false;
  appVersionLoading = false;
  appVersionError = '';
  appVersionInfo: AppVersionInfo | null = null;

  // Computed property to check if any filter is applied
  get isFiltered(): boolean {
    return this.hasActiveFilter(this.filter);
  }

  get currentFilterDisplay(): string {
    if (!this.hasActiveFilter(this.filter)) {
      return 'none';
    }

    return JSON.stringify(this.buildPrintableFilter(this.filter), null, 2);
  }

  get clearFilterButtonTitle(): string {
    return `Clear search and reset all filters\n\nCurrently active filter: ${this.currentFilterDisplay}`;
  }

  openFilterInfoDialog(): void {
    this.filterInfoDialogVisible = true;
  }

  openFilterInfoJsonDialog(): void {
    this.filterInfoJsonDialogVisible = true;
  }

  get currentFilterHumanReadable(): HumanReadableFilterItem[] {
    return this.buildHumanReadableFilter(this.filter);
  }

  // Get the count of displayed notes
  get displayedNotesCount(): number {
    return this.filteredNotes.length;
  }

  // Get the filter status message
  get filterStatusMessage(): string {
    const status = this.isFiltered ? 'Filtered' : 'Unfiltered';
    return `${status} | ${this.displayedNotesCount} record${this.displayedNotesCount !== 1 ? 's' : ''}`;
  }

  // Observable you can bind in the template (shows newest first)
  synchLog$: Observable<string[]>;
  syncDialogPinned = false;
  syncDialogAllowClose = false;

  get showSyncDialog(): boolean {
    return this._showSyncDialog;
  }

  set showSyncDialog(value: boolean) {
    if (!value && this.syncDialogPinned && !this.syncDialogAllowClose) {
      this._showSyncDialog = true;
      return;
    }
    this._showSyncDialog = value;
  }

  constructor(
    public auth: AuthService,
    private dataService: DataService,
    private router: Router,
    private route: ActivatedRoute,
    private filterService: FilterService,
    private userSettings: UserSettingsService,
    private notePrintExportService: NotePrintExportService,
    private environmentService: EnvironmentService,
    private titleService: Title,
    private cdr: ChangeDetectorRef,
    private alertService: AlertService,
    private gDS: GlobalDialogService,
    private logs: LogService,
    private syncCoordinator: SyncCoordinatorService,
    private remoteChanges: RemoteChangesService,
    private semanticSearchService: SemanticSearchService,
    private appVersionService: AppVersionService,
    private appUpdateService: AppUpdateService,

  ) {
    this.synchLog$ = this.logs.entries$('synch');
  }

  private destroy$ = new Subject<void>();
  private logAutoScrollInitialized = false;
  private syncStatusTimer: ReturnType<typeof setInterval> | null = null;
  // Static (not instance) so the fingerprint/sorted-notes cache survives
  // HomeComponent being destroyed and recreated on every navigation (there's
  // no RouteReuseStrategy) — without this, returning to Home always recomputes
  // from scratch even when nothing actually changed since you left.
  private static lastDataFingerprint: string = '';
  private static cachedSortedNotes: Note[] = [];
  private semanticSearchRequestToken = 0;

  // ─── Quick-access side panel ──────────────────────────────────────────────

  loadQuickAccessState(): void {
    this.isQaSmallScreen = typeof window !== 'undefined' && window.innerWidth < 640;
    try {
      const collapsed = localStorage.getItem(this.qaCollapsedKey);
      this.quickAccessCollapsed = collapsed === 'true';
      const savedSizes = localStorage.getItem(this.qaSizesKey);
      if (savedSizes) {
        const parsed = JSON.parse(savedSizes);
        if (Array.isArray(parsed) && parsed.length === 2) {
          this.quickAccessSizes = parsed;
          this.lastQuickAccessSizes = parsed;
        }
      }
    } catch {
      // ignore
    }
  }

  onQuickAccessResize(event: { sizes: (string | number)[] }): void {
    if (!Array.isArray(event.sizes) || event.sizes.length !== 2) {
      return;
    }
    this.quickAccessSizes = event.sizes.map((s) =>
      typeof s === 'string' ? parseFloat(s) : s,
    );
    this.lastQuickAccessSizes = [...this.quickAccessSizes];
    try {
      localStorage.setItem(this.qaSizesKey, JSON.stringify(this.quickAccessSizes));
    } catch {
      // ignore
    }
  }

  collapseQuickAccess(): void {
    this.lastQuickAccessSizes = [...this.quickAccessSizes];
    this.quickAccessCollapsed = true;
    try {
      localStorage.setItem(this.qaCollapsedKey, 'true');
    } catch {
      // ignore
    }
  }

  expandQuickAccess(): void {
    this.quickAccessCollapsed = false;
    if (this.lastQuickAccessSizes.length === 2) {
      this.quickAccessSizes = [...this.lastQuickAccessSizes];
    }
    try {
      localStorage.setItem(this.qaCollapsedKey, 'false');
    } catch {
      // ignore
    }
  }

  // ─────────────────────────────────────────────────────────────────────────

  private getDefaultFilter(): FilterParams {
    return {
      SearchString: '',
      SearchType: 'Title, Keywords & Tags',
      SemanticTopK: 10,
      Type: '',
      ProjectName: '',
      Categories: [],
      Status: '(All)',
      TagIds: [],
      ModifiedBetween: { start: null, end: null },
      CreatedBetween: { start: null, end: null },
      DateFilterField: 'modified',
      DateFilterMode: 'days',
      DateSinceDays: null,
      DateSinceDate: null,
    };
  }

  private hasRestorableFilter(candidate: unknown): candidate is FilterParams {
    if (!candidate || typeof candidate !== 'object') {
      return false;
    }

    const filterCandidate = candidate as FilterParams;
    const filterKeys: Array<keyof FilterParams> = [
      'SearchString',
      'SearchType',
      'SemanticTopK',
      'Type',
      'ProjectName',
      'Categories',
      'Status',
      'TagIds',
      'ModifiedBetween',
      'CreatedBetween',
      'DateFilterField',
      'DateFilterMode',
      'DateSinceDays',
      'DateSinceDate',
    ];

    return filterKeys.some((key) => key in filterCandidate);
  }

  private cloneFilter(filter: FilterParams | undefined | null): FilterParams {
    const source = filter ?? {};
    return {
      ...source,
      TagIds: Array.isArray(source.TagIds) ? [...source.TagIds] : [],
      Categories: Array.isArray(source.Categories) ? [...source.Categories] : [],
      ModifiedBetween: source.ModifiedBetween
        ? {
            start: source.ModifiedBetween.start ? new Date(source.ModifiedBetween.start) : null,
            end: source.ModifiedBetween.end ? new Date(source.ModifiedBetween.end) : null,
          }
        : { start: null, end: null },
      CreatedBetween: source.CreatedBetween
        ? {
            start: source.CreatedBetween.start ? new Date(source.CreatedBetween.start) : null,
            end: source.CreatedBetween.end ? new Date(source.CreatedBetween.end) : null,
          }
        : { start: null, end: null },
      DateFilterField: source.DateFilterField === 'created' ? 'created' : 'modified',
      DateFilterMode: source.DateFilterMode === 'date' ? 'date' : 'days',
      DateSinceDays:
        typeof source.DateSinceDays === 'number' && Number.isFinite(source.DateSinceDays) && source.DateSinceDays > 0
          ? Math.floor(source.DateSinceDays)
          : null,
      DateSinceDate: source.DateSinceDate ? new Date(source.DateSinceDate) : null,
    };
  }

  private hasActiveFilter(filter: FilterParams | undefined | null): boolean {
    const candidate = filter ?? {};
    const normalize = (value?: string | null): string => (value ?? '').trim();
    const hasDate = (value?: { start: Date | null; end: Date | null } | null): boolean => {
      const start = value?.start instanceof Date && !Number.isNaN(value.start.getTime()) ? value.start : null;
      const end = value?.end instanceof Date && !Number.isNaN(value.end.getTime()) ? value.end : null;
      return !!start || !!end;
    };

    const activeCategories = Array.isArray(candidate.Categories)
      ? candidate.Categories.filter((id) => typeof id === 'string' && id.trim().length > 0)
      : [];
    const allCategoryIds = this.categories
      .map((category) => category._id)
      .filter((id) => typeof id === 'string' && id.trim().length > 0);
    const hasCategoryFilter =
      activeCategories.length > 0 &&
      (allCategoryIds.length === 0 || !this.areStringSetsEqual(activeCategories, allCategoryIds));
    const hasDateSinceDays =
      typeof candidate.DateSinceDays === 'number' && Number.isFinite(candidate.DateSinceDays) && candidate.DateSinceDays > 0;
    const hasDateSinceDate =
      candidate.DateSinceDate instanceof Date && !Number.isNaN(candidate.DateSinceDate.getTime());

    return !!(
      normalize(candidate.SearchString).length > 0 ||
      normalize(candidate.Type).length > 0 ||
      normalize(candidate.ProjectName).length > 0 ||
      (Array.isArray(candidate.TagIds) && candidate.TagIds.some((id) => typeof id === 'string' && id.trim().length > 0)) ||
      (Array.isArray(candidate.ExcludeTagIds) &&
        candidate.ExcludeTagIds.some((id) => typeof id === 'string' && id.trim().length > 0)) ||
      hasCategoryFilter ||
      (normalize(candidate.Status).length > 0 && normalize(candidate.Status) !== '(All)') ||
      hasDateSinceDays ||
      hasDateSinceDate ||
      hasDate(candidate.ModifiedBetween) ||
      hasDate(candidate.CreatedBetween)
    );
  }

  private buildPrintableFilter(filter: FilterParams | undefined | null): Record<string, unknown> {
    const candidate = filter ?? {};
    const normalize = (value?: string | null): string => (value ?? '').trim();
    const formatDate = (value?: Date | null): string | null => {
      if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
        return null;
      }
      return value.toISOString();
    };
    const tagLabelById = new Map(this.availableTags.map((tag) => [tag._id, tag.name]));
    const categoryLabelById = new Map(this.categories.map((category) => [category._id, category.Path || category.Name]));
    const describeIds = (ids: string[] | undefined, lookup: Map<string, string>) =>
      (Array.isArray(ids) ? ids : [])
        .filter((id) => typeof id === 'string' && id.trim().length > 0)
        .map((id) => ({
          id,
          label: lookup.get(id) ?? id,
        }));

    const activeCategories = Array.isArray(candidate.Categories)
      ? candidate.Categories.filter((id) => typeof id === 'string' && id.trim().length > 0)
      : [];
    const allCategoryIds = this.categories
      .map((category) => category._id)
      .filter((id) => typeof id === 'string' && id.trim().length > 0);
    const categoriesSelection =
      activeCategories.length === 0 || this.areStringSetsEqual(activeCategories, allCategoryIds)
        ? {
            mode: 'all',
            count: allCategoryIds.length,
          }
        : {
            mode: 'selected',
            values: describeIds(activeCategories, categoryLabelById),
          };

    return {
      SearchString: normalize(candidate.SearchString),
      SearchType: normalize(candidate.SearchType) || 'Title, Keywords & Tags',
      SemanticTopK: Number.isFinite(candidate.SemanticTopK) ? Number(candidate.SemanticTopK) : null,
      IncludeTags: describeIds(candidate.TagIds, tagLabelById),
      TagIncludeMode: candidate.TagIncludeMode === 'OR' ? 'OR' : 'AND',
      ExcludeTags: describeIds(candidate.ExcludeTagIds, tagLabelById),
      Categories: categoriesSelection,
      Type: normalize(candidate.Type),
      ProjectName: normalize(candidate.ProjectName),
      Status: normalize(candidate.Status) || '(All)',
      DateFilter: {
        field: candidate.DateFilterField === 'created' ? 'Creation Date' : 'Modified Date',
        mode: candidate.DateFilterMode === 'date' ? 'Since Date' : 'Within Days',
        sinceDays:
          typeof candidate.DateSinceDays === 'number' && Number.isFinite(candidate.DateSinceDays) && candidate.DateSinceDays > 0
            ? Math.floor(candidate.DateSinceDays)
            : null,
        sinceDate: formatDate(candidate.DateSinceDate ?? null),
      },
      ModifiedBetween: {
        start: formatDate(candidate.ModifiedBetween?.start ?? null),
        end: formatDate(candidate.ModifiedBetween?.end ?? null),
      },
      CreatedBetween: {
        start: formatDate(candidate.CreatedBetween?.start ?? null),
        end: formatDate(candidate.CreatedBetween?.end ?? null),
      },
    };
  }

  private buildHumanReadableFilter(filter: FilterParams | undefined | null): HumanReadableFilterItem[] {
    const candidate = filter ?? {};
    const normalize = (value?: string | null): string => (value ?? '').trim();
    const rows: HumanReadableFilterItem[] = [];

    const includeTagIds = Array.isArray(candidate.TagIds)
      ? candidate.TagIds.filter((id) => typeof id === 'string' && id.trim().length > 0)
      : [];
    const excludeTagIds = Array.isArray(candidate.ExcludeTagIds)
      ? candidate.ExcludeTagIds.filter((id) => typeof id === 'string' && id.trim().length > 0)
      : [];
    const activeCategories = Array.isArray(candidate.Categories)
      ? candidate.Categories.filter((id) => typeof id === 'string' && id.trim().length > 0)
      : [];
    const allCategoryIds = this.categories
      .map((category) => category._id)
      .filter((id) => typeof id === 'string' && id.trim().length > 0);
    const hasCategoryFilter =
      activeCategories.length > 0 &&
      (allCategoryIds.length === 0 || !this.areStringSetsEqual(activeCategories, allCategoryIds));

    const tagLabelById = new Map(this.availableTags.map((tag) => [tag._id, tag.name]));
    const categoryLabelById = new Map(this.categories.map((category) => [category._id, category.Path || category.Name]));

    const namesFromIds = (ids: string[], lookup: Map<string, string>) =>
      ids.map((id) => lookup.get(id) || id).filter((name) => !!name);

    const formatDate = (value?: Date | null): string => {
      if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
        return '';
      }
      return value.toLocaleDateString('en-CA');
    };

    const modifiedFrom = formatDate(candidate.ModifiedBetween?.start ?? null);
    const modifiedTo = formatDate(candidate.ModifiedBetween?.end ?? null);
    const createdFrom = formatDate(candidate.CreatedBetween?.start ?? null);
    const createdTo = formatDate(candidate.CreatedBetween?.end ?? null);
    const dateFieldLabel = candidate.DateFilterField === 'created' ? 'Created' : 'Modified';
    const sinceDays =
      typeof candidate.DateSinceDays === 'number' && Number.isFinite(candidate.DateSinceDays) && candidate.DateSinceDays > 0
        ? Math.floor(candidate.DateSinceDays)
        : null;
    const sinceDate = formatDate(candidate.DateSinceDate ?? null);
    const searchType = normalize(candidate.SearchType) || 'Title, Keywords & Tags';
    const status = normalize(candidate.Status);

    const searchString = normalize(candidate.SearchString);
    if (searchString) {
      rows.push({ label: 'Search Text', value: searchString });
      rows.push({ label: 'Search In', value: searchType });
      if (searchType.toLowerCase() === 'semantic' && Number.isFinite(candidate.SemanticTopK)) {
        rows.push({ label: 'Semantic Top K', value: String(Number(candidate.SemanticTopK)) });
      }
    }

    const typeValue = normalize(candidate.Type);
    if (typeValue) {
      rows.push({ label: 'Type', value: typeValue });
    }

    const projectName = normalize(candidate.ProjectName);
    if (projectName) {
      rows.push({ label: 'Project', value: projectName });
    }

    if (status && status !== '(All)') {
      rows.push({ label: 'Status', value: status });
    }

    if (includeTagIds.length > 0) {
      rows.push({
        label: 'Include Tags',
        value: namesFromIds(includeTagIds, tagLabelById).join(', '),
      });
      if (includeTagIds.length > 1) {
        rows.push({
          label: 'Include Tag Logic',
          value: candidate.TagIncludeMode === 'OR' ? 'Match any selected tag (OR)' : 'Match all selected tags (AND)',
        });
      }
    }

    if (excludeTagIds.length > 0) {
      rows.push({
        label: 'Exclude Tags',
        value: namesFromIds(excludeTagIds, tagLabelById).join(', '),
      });
    }

    if (hasCategoryFilter) {
      rows.push({
        label: 'Categories',
        value: namesFromIds(activeCategories, categoryLabelById).join(', '),
      });
    }

    if (sinceDays) {
      rows.push({
        label: 'Date Filter',
        value: `${dateFieldLabel} within ${sinceDays} ${sinceDays === 1 ? 'day' : 'days'}`,
      });
    } else if (sinceDate) {
      rows.push({
        label: 'Date Filter',
        value: `${dateFieldLabel} since ${sinceDate}`,
      });
    } else if (modifiedFrom || modifiedTo) {
      rows.push({
        label: 'Modified Date',
        value: modifiedFrom && modifiedTo ? `${modifiedFrom} to ${modifiedTo}` : modifiedFrom ? `From ${modifiedFrom}` : `Until ${modifiedTo}`,
      });
    } else if (createdFrom || createdTo) {
      rows.push({
        label: 'Created Date',
        value: createdFrom && createdTo ? `${createdFrom} to ${createdTo}` : createdFrom ? `From ${createdFrom}` : `Until ${createdTo}`,
      });
    }

    return rows.filter((row) => row.value.trim().length > 0);
  }

  private areStringSetsEqual(left: string[], right: string[]): boolean {
    if (left.length !== right.length) {
      return false;
    }

    const leftSorted = [...left].sort();
    const rightSorted = [...right].sort();
    return leftSorted.every((value, index) => value === rightSorted[index]);
  }

  private restoreFilter(navState: Record<string, unknown>): FilterParams {
    const navigationFilter = navState['filter'];
    const persistedFilter = this.filterService.getFilterParams();
    const restored = this.hasRestorableFilter(navigationFilter)
      ? navigationFilter
      : this.hasRestorableFilter(persistedFilter)
        ? persistedFilter
        : this.getDefaultFilter();

    const filter = this.cloneFilter(restored);
    if (!filter.SearchType || filter.SearchType.trim() === '') {
      filter.SearchType = 'Title, Keywords & Tags';
    }
    if (!filter.SemanticTopK || filter.SemanticTopK < 1) {
      filter.SemanticTopK = 10;
    }
    return filter;
  }

  private persistActiveFilter(): void {
    this.filterService.setFilterParams(this.cloneFilter(this.filter));
  }

  async ngOnInit() {
    try {
      console.log('HomeComponent - in ngOnInit()');
      const navState = this.router.getCurrentNavigation()?.extras?.state ?? (history.state || {});
      const requestedViewFromNav = navState['view'];
      // Router navigation `state` doesn't survive a window.open() into a brand-new
      // browsing context (e.g. the header's "New Calendar window" button) — a URL
      // query param is the only way to request a starting view across windows.
      const requestedViewFromQuery = this.route.snapshot.queryParamMap.get('view');
      const requestedHomeState = navState['homeState'] as HomeUiState | undefined;
      const persistedHomeState = this.readPersistedHomeUiState();
      const requestedView =
        (typeof requestedViewFromNav === 'string' && requestedViewFromNav.trim() !== ''
          ? requestedViewFromNav
          : undefined) ??
        (requestedViewFromQuery && requestedViewFromQuery.trim() !== '' ? requestedViewFromQuery : undefined) ??
        (typeof requestedHomeState?.view === 'string' && requestedHomeState.view.trim() !== ''
          ? requestedHomeState.view
          : undefined);

      this.calendarMonthState =
        (typeof requestedHomeState?.calendarMonth === 'string' && requestedHomeState.calendarMonth.trim() !== ''
          ? requestedHomeState.calendarMonth
          : null) ??
        (typeof persistedHomeState.calendarMonth === 'string' && persistedHomeState.calendarMonth.trim() !== ''
          ? persistedHomeState.calendarMonth
          : null);

      this.calendarVisibilityState =
        requestedHomeState?.calendarVisibility === 'all' || requestedHomeState?.calendarVisibility === 'calendar'
          ? requestedHomeState.calendarVisibility
          : persistedHomeState.calendarVisibility === 'all' || persistedHomeState.calendarVisibility === 'calendar'
            ? persistedHomeState.calendarVisibility
            : 'calendar';

      this.updateTemplateMenuItems();
      this.setTitle('NotesApp');
      this.alertService.clear();
      this.loadQuickAccessState();
      void this.loadAppVersionInfo();
      // Render immediately from local caches; refresh remote-dependent data in background.
      this.availableTags = Array.isArray(this.dataService.allTags)
        ? [...this.dataService.allTags]
        : [];
      void this.loadTagCatalogInBackground();
      void this.refreshUserSettingsInBackground();

      // 2️⃣ Restore or initialize filter
      this.filter = this.restoreFilter(navState as Record<string, unknown>);
      this.persistActiveFilter();
      // 3️⃣ Watch user settings
      this.userSettings.userSettings$.pipe(takeUntil(this.destroy$)).
        subscribe((settings) => {
        if (!settings) return;
        console.log('UserSettings loaded:', settings);
        if (settings.primaryColor) {
          this.layoutService.appState.update((state: any) => ({
            ...state,
            primary: settings.primaryColor,
          }));
        }
        // Restore the last active view
        if (!requestedView && settings.homeView) {
          this.selectedView = settings.homeView;
          this.persistHomeUiState();
          this.updateHomeTitle();
        }
      });
      if (typeof requestedView === 'string' && requestedView.trim() !== '') {
        this.selectedView = requestedView;
      } else if (typeof persistedHomeState.view === 'string' && persistedHomeState.view.trim() !== '') {
        this.selectedView = persistedHomeState.view;
      }
      this.persistHomeUiState();
      this.updateHomeTitle();
      this.userSettings.systemZoomFactor$.pipe(takeUntil(this.destroy$)).subscribe((factor) => {
        const safeFactor = typeof factor === 'number' && !Number.isNaN(factor) ? factor : 100;
        console.log('HomeComponent: system zoom factor =', safeFactor);
        this.systemZoomFactor = safeFactor;
        this.homeZoomScale = safeFactor / 100;
      });

      // 4️⃣ Network status - subscribe to online/offline changes
      this.environmentService.online$
        .pipe(takeUntil(this.destroy$))
        .subscribe((status) => {
          this.isOnline = status;
          this.refreshSyncStatus();
          this.updateSyncStatusMessage();
          this.cdr.markForCheck();
        });

      // Server reachability - distinct from browser online/offline (see SyncStateLogic):
      // the browser can report "online" while the app's server is unreachable.
      this.syncCoordinator.serverReachable$
        .pipe(takeUntil(this.destroy$))
        .subscribe((reachable) => {
          this.serverReachable = reachable;
          this.refreshSyncStatus();
          this.updateSyncStatusMessage();
          this.cdr.markForCheck();
        });

      // 5️⃣ Init projects / statuses
      this.projects = this.dataService.getAllProjects();
      this.statusValues = this.dataService.getAllStatuses();
      this.types = this.dataService.getAllNoteTypes();
      this.initializeBulkActions();

      const lastSync = this.syncCoordinator.getLastSyncCompleted();
      if (lastSync) {
        this.syncStatus = { ...this.syncStatus, lastSynced: lastSync };
      }
      this.syncCoordinator
        .onSyncCompleted()
        .pipe(takeUntil(this.destroy$))
        .subscribe((time) => {
          this.syncStatus = { ...this.syncStatus, lastSynced: time };
          this.updateSyncStatusMessage();
          void this.dataService.refreshLocalData();
        });
      this.updateSyncStatusMessage();
      if (!this.syncStatusTimer) {
        this.syncStatusTimer = setInterval(() => this.updateSyncStatusMessage(), 60_000);
      }

      this.dataService.dataReloaded$
        .pipe(
          filter((stamp): stamp is Date => stamp instanceof Date),
          takeUntil(this.destroy$),
        )
        .subscribe(() => this.refreshFromMemory());

      // 6️⃣ Wait for data load and fetch notes + categories
      this.dataService.dataLoaded$
        .pipe(
          distinctUntilChanged(), // only fire on false→true transition, not on every sync reload
          filter(Boolean),
          switchMap(() => this.dataService.getAllNotesAndCategories()),
          takeUntil(this.destroy$),
        )
        .subscribe({
          next: (response: ApiResponse<{ notes: Note[]; categories: Category[] }>) => {
            if (response.status === 'success' && response.data) {
              const { notes, categories } = response.data;
              console.log('✅ Notes and categories loaded:', notes.length, 'notes,', categories.length, 'categories');

              // Skip the full re-sort when the data is identical to what the
              // last (possibly destroyed) HomeComponent instance already
              // computed — common when returning via the Close button.
              const fingerprint = this.createDataFingerprint(notes, categories);
              if (fingerprint === HomeComponent.lastDataFingerprint && HomeComponent.cachedSortedNotes.length === notes.length) {
                console.log('[ngOnInit] Data unchanged since last load, reusing cached sort');
                this.allNotes = HomeComponent.cachedSortedNotes;
              } else {
                this.allNotes = notes.sort(
                  (a: Note, b: Note) =>
                    new Date(b.created_on).getTime() - new Date(a.created_on).getTime(),
                );
                HomeComponent.lastDataFingerprint = fingerprint;
                HomeComponent.cachedSortedNotes = this.allNotes;
              }

              this.categories = categories;
              this.categoryItems = categories.map((c: Category) => ({
                ID: c._id,
                ItemName: c.Path,
                isSelected: false,
              }));

              this.assignSortOrderToProjects(this.projects, this.allNotes,
                this.card_sort);
              this.sortProjectsByCardSort();
              this.sortNotesByCardSort();
              this.applyFilters();
              this.cdr.detectChanges();
            } else {
              const msg = response.error?.message || 
                'Failed to load notes/categories.';
              console.warn('⚠️ Load failed:', msg);
              this.alertService?.warn?.(msg, { life: 4000 });
            }
          },
          error: (err) => {
            console.error('Failed to load notes/categories:', err);
            this.alertService?.error?.(
              err?.message || 'Could not load local notes/categories.',
              { life: 5000 },
            );
          },
          complete: () => {
            // no-op
          },
        });

      // 7️⃣ Handle refresh
      if (history.state?.refresh) this.getItems();

      console.log('ngOnInit complete');
    } catch (err: any) {
      console.error('Unexpected initialization error:', err);
      this.gDS.showGlobalDialog(
        'Critical Error',
        'Initialization failed',
        err?.message || 'Unexpected app crash.',
        DialogType.Error,
      );
    }
  }

  ngAfterViewInit(): void {
    if (this.logAutoScrollInitialized) return;
    this.logAutoScrollInitialized = true;
    this.synchLog$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.scrollSyncLogToBottom();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    UIStateLogic.updateBodySelectModeClass(false);
    if (this.syncStatusTimer) {
      clearInterval(this.syncStatusTimer);
      this.syncStatusTimer = null;
    }
  }


  // ##############################################################################
  // DATA LOADING FUNCTIONS
  // ##############################################################################

  get isLoading(): boolean {
    return this.loadingCounter > 0;
  }

  getItems(): void {
    this.loadingCounter++; // Start loading
    console.log('*** in getItems() ***');

    this.dataService.getAllNotes().subscribe({
      next: (response) => {
        if (response.status === 'success' && response.data) {
          this.allNotes = response.data.sort((a, b) => {
            const dateA = new Date(a.created_on);
            const dateB = new Date(b.created_on);
            return dateB.getTime() - dateA.getTime(); // DESC by created_on
          });

          // Perform your existing post-processing steps
          this.assignSortOrderToProjects(this.projects, this.allNotes, 
            this.card_sort);
          this.sortProjectsByCardSort();
          this.sortNotesByCardSort();
          this.applyFilters();
        } else {
          const msg = response.error?.message || 
            'Failed to load notes (unknown error).';
          console.warn('⚠️ getItems():', msg);
          this.alertService?.warn?.(msg, { life: 4000 });
        }

        this.loadingCounter--; // Always stop loading, even on handled error
      },

      error: (err) => {
        console.error('❌ getItems(): unexpected error:', err);
        this.alertService?.error?.('An unexpected error occurred while loading notes.', 
            { life: 5000 });
        this.loadingCounter--; // Ensure loading flag decrements
      },
    });
  }

  // ##############################################################################
  // SYNCH-RELATED ROUTINES
  // ##############################################################################

  /** Open the dialog and refresh current sync status */
  openSyncDialog(): void {
    console.log('HomeComponent: openSyncDialog()');
    this.syncDialogPinned = true;
    this.syncDialogAllowClose = false;
    this.showSyncDialog = true;
    this.refreshSyncStatus();
    // this.dataService.fetchRemoteDbInfoAndLogActive();
    // this.dataService.fetchOfflineDbInfoAndLogActive();
    // setTimeout(() => this.scrollSyncLogToBottom(), 0);
  }

  getDbInfos(): void {
    this.refreshSyncStatus();
    this.dataService.fetchRemoteDbInfoAndLogActive();
    this.dataService.fetchOfflineDbInfoAndLogActive();
  }


  /** Close the dialog */
  closeSyncDialog(): void {
    this.syncDialogPinned = false;
    this.syncDialogAllowClose = true;
    this.showSyncDialog = false;
  }

  onSyncDialogHide(): void {
    if (this.syncDialogAllowClose) {
      this.syncDialogAllowClose = false;
      this.showSyncDialog = false;
      return;
    }
    if (this.syncDialogPinned) {
      setTimeout(() => {
        this.showSyncDialog = true;
      }, 0);
      return;
    }
    this.showSyncDialog = true;
  }

  /** Pull latest statuses from your services */
  refreshSyncStatus(): void {
    // Preserve lastSynced — SyncStateLogic.refreshSyncStatus() only recomputes
    // the couchdb/indexeddb status from current connectivity, it doesn't know
    // about the last successful sync time tracked separately via onSyncCompleted().
    this.syncStatus = {
      ...SyncStateLogic.refreshSyncStatus(this.isOnline, this.serverReachable),
      lastSynced: this.syncStatus.lastSynced,
    };
  }

  /** While syncing, show transient 'syncing' states without 
   * losing offline/error info permanently */
  private setTransientStates(state: SyncState): void {
    this.syncStatus = SyncStateLogic.setTransientStates(this.syncStatus, state);
  }

  private updateSyncStatusMessage(): void {
    // The "OFFLINE" / "Server not responding" state itself is conveyed by the footer's
    // red/green icon and its mouse-over tooltip (connectivity.label) — kept out of this
    // visible text so the footer bar doesn't grow every time connectivity drops.
    const lastSynced = this.syncStatus.lastSynced;
    if (!lastSynced) {
      this.globalStatusMessage = `Last sync: never`;
      return;
    }

    const diffMs = Date.now() - lastSynced.getTime();
    const minutes = Math.max(0, Math.floor(diffMs / 60000));

    if (minutes < 1) {
      this.globalStatusMessage = `Last sync: just now`;
      return;
    }
    if (minutes < 60) {
      this.globalStatusMessage = `Last sync: ${minutes}m ago`;
      return;
    }
    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
      this.globalStatusMessage = `Last sync: ${hours}h ago`;
      return;
    }
    const days = Math.floor(hours / 24);
    this.globalStatusMessage = `Last sync: ${days}d ago`;
  }

  trackByIndex(i: number): number {
    return i;
  }

  /** Notes with local changes not yet confirmed pushed to the server (new/updated/deleted). */
  get unsyncedNotesCount(): number {
    return this.allNotes.reduce(
      (count, note) => count + (note.syncStatus && note.syncStatus !== 'synced' ? 1 : 0),
      0,
    );
  }

  /** Message shown in the footer's connectivity popover — makes the user aware of how
   * many local changes are still waiting to reach the server while offline. */
  get connectivityPopoverMessage(): string {
    const count = this.unsyncedNotesCount;
    const pending = count > 0
      ? `${count} note${count === 1 ? '' : 's'} with unsynced local change${count === 1 ? '' : 's'}.`
      : 'No unsynced local changes.';

    if (this.connectivity.level === 'offline') {
      return `NotesApp is offline. ${pending}`;
    }
    if (this.connectivity.level === 'server-unreachable') {
      return `Server is not responding. ${pending}`;
    }
    return `Online — everything is in sync. ${pending}`;
  }

  // src/app/_components/home/home.component.ts

  synchNow(): void {
  this.isSyncing = true;
  this.setTransientStates('syncing');
  this.logs.append('synch', '[MANUAL] incremental sync requested');

  this.syncCoordinator
    .forceIncrementalSync('manual')
    .then(() => this.refreshData())
    .finally(() => (this.isSyncing = false));
}


async forceBaseline() {
  await this.syncCoordinator.runManualBaseline();
}

  async forceBaselineConfirmed(): Promise<void> {
    const ok = confirm(
      'Force baseline sync? This clears IndexedDB and re-downloads all notes.',
    );
    if (!ok) return;
    await this.forceBaseline();
  }


// synchNow(): void {
//   this.isSyncing = true;

//   this.dataService
//     .syncAllChanges('manual')
//     .catch((err) => console.error('Manual sync failed', err))
//     .finally(() => {
//       this.isSyncing = false;
//     });
// }

//   async synchNow(): Promise<void> {
//     console.log('home: in synchNow()');
//     if (this.isSyncing) return;

//     this.isSyncing = true;
//     this.setTransientStates('syncing');
//     this.loadingCounter++;

//     try {
//       await this.dataService.syncAllChanges();
//       console.log('Data synchronized successfully');
//       this.getItems(); // Refresh items after synchronization
//       this.alertService.success('Synchronization successful');

//       this.syncStatus = SyncStateLogic.setSuccessState(this.syncStatus);
//     } catch (err: any) {
//       this.logs.append('synch', 'Sync failed!');
//       this.syncStatus = SyncStateLogic.setErrorState(this.syncStatus);
//       this.gDS.showGlobalDialog(
//         'Synchronization Error',
//         'Failed to synchronize data',
//         err?.message || 'An unknown error occurred.',
//         DialogType.Error,
//       );
//       this.logs.append('synch', err?.message || 'An unknown error occurred.');
//     } finally {
//       this.loadingCounter--;
//       this.isSyncing = false;
//     }
//   }

  clearOfflineStorage(): void {
    const ok = confirm(
        'Are you sure you want to clear all offline storage? This action cannot be undone.');
    if (!ok) return;
    console.log('Clearing offline storage...');
    this.dataService.clearOfflineStorage();
  }

  clearSynchLog(): void {
    this.logs.clear('synch');
  }

  resetSyncCursor(): void {
    const ok = confirm(
      'Reset sync cursor to 0? The next incremental sync will scan from the start.',
    );
    if (!ok) return;
    this.remoteChanges.resetLastSeq();
  }

  private scrollSyncLogToBottom(): void {
    if (!this.syncLogContainer?.nativeElement) {
      return;
    }
    const element = this.syncLogContainer.nativeElement;
    const schedule =
      typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function'
        ? window.requestAnimationFrame.bind(window)
        : (cb: FrameRequestCallback) => setTimeout(() => cb(Date.now()), 0);
    schedule(() => {
      element.scrollTop = element.scrollHeight;
    });
  }

  // ##############################################################################
  // PRESENTATION LAYER / GUI ROUTINES (CALL-BACK ETC.)
  // ##############################################################################

  // called from header component
  onSearchChanged(query: string) {
    //console.log("SearchString: ",searchText);
    this.filter.SearchString = query;
    this.persistActiveFilter();
    this.applyFilters(); // Apply filters when filter changes
  }

  // from header component
  onFilterChanged(filter: any) {
    this.filter = this.cloneFilter(filter);
    console.log('HomeComponent: onFilterChanged(): filter = ', this.filter);
    this.persistActiveFilter();
    this.applyFilters();
  }

  onCategoryClicked(category: Category) {
    this.filter.Categories = [category._id];
    this.persistActiveFilter();
    this.applyFilters();
    this.changeView('List');
    this.userSettings.setLocal({ homeView: 'List' });
  }

  onTagClicked(tag: Tag) {
    this.filter.TagIds = [tag._id];
    this.persistActiveFilter();
    this.applyFilters();
    if (this.selectedView !== 'Tags') {
      this.changeView('List');
      this.userSettings.setLocal({ homeView: 'List' });
    }
  }

  onQuickAccessRelatedTagSelected(tagId: string): void {
    if (typeof tagId !== 'string' || !tagId.trim()) {
      return;
    }

    const normalizedTagId = tagId.trim();
    const nextFilter = this.cloneFilter(this.filter);
    const nextTagIds = new Set(Array.isArray(nextFilter.TagIds) ? nextFilter.TagIds : []);
    nextTagIds.add(normalizedTagId);

    nextFilter.TagIds = Array.from(nextTagIds);
    nextFilter.TagIncludeMode = 'AND';
    nextFilter.ExcludeTagIds = Array.isArray(nextFilter.ExcludeTagIds)
      ? nextFilter.ExcludeTagIds.filter((id) => id !== normalizedTagId)
      : [];

    this.filter = nextFilter;
    this.persistActiveFilter();
    this.applyFilters();
  }

  onQuickAccessRelatedTagRemoved(tagId: string): void {
    if (typeof tagId !== 'string' || !tagId.trim()) {
      return;
    }

    const normalizedTagId = tagId.trim();
    const nextFilter = this.cloneFilter(this.filter);
    const nextTagIds = Array.isArray(nextFilter.TagIds)
      ? nextFilter.TagIds.filter((id) => id !== normalizedTagId)
      : [];

    nextFilter.TagIds = nextTagIds;
    nextFilter.TagIncludeMode = 'AND';

    this.filter = nextFilter;
    this.persistActiveFilter();
    this.applyFilters();
  }

  onQuickAccessRelatedTagExcluded(tagId: string): void {
    if (typeof tagId !== 'string' || !tagId.trim()) {
      return;
    }

    const normalizedTagId = tagId.trim();
    const nextFilter = this.cloneFilter(this.filter);
    const nextExcluded = new Set(Array.isArray(nextFilter.ExcludeTagIds) ? nextFilter.ExcludeTagIds : []);
    nextExcluded.add(normalizedTagId);

    nextFilter.ExcludeTagIds = Array.from(nextExcluded);
    nextFilter.TagIds = Array.isArray(nextFilter.TagIds)
      ? nextFilter.TagIds.filter((id) => id !== normalizedTagId)
      : [];
    nextFilter.TagIncludeMode = 'AND';

    this.filter = nextFilter;
    this.persistActiveFilter();
    this.applyFilters();
  }

  toggleDarkMode() {
    UIStateLogic.toggleDarkMode(this.layoutService);
  }

  setCurrentView(view: string) {
    this.userSettings.setLocal({ homeView: view });
    this.selectedView = view;
    if (view === 'Calendar' && !this.calendarMonthState) {
      this.calendarMonthState = this.toMonthKey(new Date());
    }
    this.persistHomeUiState();
    this.updateHomeTitle();
  }

  onCalendarMonthChanged(monthKey: string): void {
    if (typeof monthKey !== 'string' || monthKey.trim() === '') {
      return;
    }
    this.calendarMonthState = monthKey;
    this.persistHomeUiState();
  }

  onCalendarVisibilityChanged(mode: 'calendar' | 'all'): void {
    this.calendarVisibilityState = mode === 'all' ? 'all' : 'calendar';
    this.persistHomeUiState();
  }

  private toMonthKey(date: Date): string {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    return `${year}-${month}`;
  }

  private readPersistedHomeUiState(): HomeUiState {
    try {
      const raw = sessionStorage.getItem(HOME_UI_STATE_STORAGE_KEY);
      if (!raw) {
        return {};
      }
      const parsed = JSON.parse(raw) as HomeUiState;
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }

  private persistHomeUiState(): void {
    try {
      const state: HomeUiState = {
        view: this.selectedView,
        calendarMonth: this.calendarMonthState || undefined,
        calendarVisibility: this.calendarVisibilityState,
      };
      sessionStorage.setItem(HOME_UI_STATE_STORAGE_KEY, JSON.stringify(state));
    } catch {
      // ignore storage failures
    }
  }

  onPageMenuAction(item: string): void {
    const actions: Record<string, () => void> = {
      New: () => this.createNewNote(),
      NewFromTemplate: () => this.openTemplateDialog(),
      List: () => this.setCurrentView('List'),
      CardView: () => this.setCurrentView('CardView'),
      Categories: () => this.setCurrentView('Categories'),
      Tags: () => this.setCurrentView('Tags'),
      Projects: () => this.setCurrentView('Projects'),
      Calendar: () => this.setCurrentView('Calendar'),
      Refresh: () => this.refreshApp(),
      Select: () => this.selectMode(),
      DownloadJson: () => this.downloadNotesAsJson(),
      UserSettings: () => this.goToUserSettings(),
      AppInfo: () => this.openAppInfoDialog(),
      CheckForUpdate: () => this.appUpdateService.checkForUpdatesNow(),
      Admin: () => this.goToAdmin(),
      Synchronisation: () => this.openSyncDialog(),
      AI: () => {
        const filteredNoteIds = this.isFiltered
          ? [...new Set((this.filteredNotes || []).map((note) => String(note?._id ?? '').trim()).filter((id) => !!id))]
          : [];
        this.aiSessionService.openDialog({ filteredNoteIds });
      },
      DebugAI: () => this.router.navigate(['/ai/administration']),
      ReportNotesappErrors: () => this.router.navigate(['/reports/notesapp-errors']),
      ReportAiRequestsLog: () => this.router.navigate(['/reports/ai-requests-log']),
      ReportAiLimits: () => this.router.navigate(['/reports/ai-limits']),
      Logout: () => this.logout(),
    };

    const action = actions[item];
    if (action) {
      action();
    } else {
      console.warn(`Unhandled menu item: ${item}`);
    }
  }

  // refactor filter-menu code
  onSelectFilterMenuItem(selectedItem: string): void {
    this.applyFilters(); // Apply filters when filter changes
  }

  setTitle(newTitle: string) {
    this.titleService.setTitle(newTitle);
  }

  private homeViewLabel(view: string): string {
    return view === 'CardView' ? 'Cards' : view;
  }

  private updateHomeTitle(): void {
    this.setTitle(`NotesApp - Home (${this.homeViewLabel(this.selectedView)})`);
  }

  assignSortOrderToProjects(projects: Project[] | undefined, noteItems: Note[], 
        card_sort: CardSort[]) {
    SortingLogic.assignSortOrderToProjects(projects, noteItems, card_sort);
  }

  sortNotesByCardSort(): void {
    this.filteredNotes = SortingLogic.sortNotesByCardSort(this.filteredNotes, this.card_sort);
  }

  sortProjectsByCardSort(): void {
    SortingLogic.sortProjectsByCardSort(this.projects, this.card_sort);
  }

//   getNoteYearPrefix(note: Note): string {
//     return FilterLogic.getNoteYearPrefix(note);
//   }

  getLastModifiedNotes(projectId: number): Note[] {
    return FilterLogic.getLastModifiedNotes(projectId, this.filteredNotes);
  }

  // count the number of filtered notes for a project
  calculateItemCount(projectId: number): number {
    return FilterLogic.calculateItemCount(projectId, this.filteredNotes);
  }

  viewAllNotes(projectId: number): void {
    // this.router.navigate(['/project', projectId]);
  }

  toggleHeaderVisibility(): void {
    this.isHeaderVisible = UIStateLogic.toggleHeaderVisibility(this.isHeaderVisible);
  }

  clickCardHeader(proj: Project): void {
    NavigationLogic.clickCardHeader(this.router, proj, this.filter);
  }

  clickRow(row: Note) {
    NavigationLogic.clickRow(this.router, row, this.filter);
  }

  isVisible(element: HTMLElement): boolean {
    const style = window.getComputedStyle(element);
    return element.offsetParent !== null && style.visibility !== 'hidden' 
        && style.display !== 'none';
  }

  // ##############################################################################
  // NAVIGATION FUNCTIONS
  // ##############################################################################

  changeView(view: string): void {
    console.log('changeView to:', view);
    this.selectedView = view;
    this.updateHomeTitle();
  }

  navigateToEdit(row: Note) {
    NavigationLogic.navigateToEdit(this.router, row, this.filter);
  }

  logout(): void {
    NavigationLogic.logout(this.router, this.auth);
  }

  goToAdmin(): void {
    NavigationLogic.goToAdmin(this.router, this.filter);
  }
  goToUserSettings(): void {
    NavigationLogic.goToUserSettings(this.router, this.filter);
  }

  openAppInfoDialog(): void {
    this.appInfoDialogVisible = true;
    if (!this.appVersionInfo && !this.appVersionLoading) {
      void this.loadAppVersionInfo();
    }
  }

  refreshAppInfoDialog(): void {
    void this.loadAppVersionInfo(true);
  }

  get appVersionDisplayBuiltAt(): string {
    const builtAt = this.appVersionInfo?.builtAtUtc;
    if (!builtAt) {
      return 'Not available';
    }
    const parsed = new Date(builtAt);
    return Number.isNaN(parsed.getTime()) ? builtAt : parsed.toLocaleString();
  }

  private async loadAppVersionInfo(forceRefresh = false): Promise<void> {
    this.appVersionLoading = true;
    this.appVersionError = '';
    try {
      this.appVersionInfo = await this.appVersionService.getVersionInfo(forceRefresh);
    } catch (err: any) {
      this.appVersionError = err?.message || 'Unable to load application version info.';
    } finally {
      this.appVersionLoading = false;
    }
  }

  // ##############################################################################
  // MISC LOGIC FUNCTIONS
  // ##############################################################################

  downloadNotesAsJson(): void {
    this.dataService.getAllNotes().subscribe({
      next: (data) => {
        const jsonStr = JSON.stringify(data, null, 2);
        const blob = new Blob([jsonStr], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const now = new Date();
        const timestamp = now.getFullYear().toString() +
          (now.getMonth() + 1).toString().padStart(2, '0') +
          now.getDate().toString().padStart(2, '0') + '-' +
          now.getHours().toString().padStart(2, '0') +
          now.getMinutes().toString().padStart(2, '0') +
          now.getSeconds().toString().padStart(2, '0');
        const a = document.createElement('a');
        a.href = url;
        a.download = `NotesAppData_Backup_${timestamp}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      },
      error: (err) => {
        console.error('Error downloading notes as JSON:', err);
        this.alertService?.error?.('Failed to download notes as JSON.', { life: 5000 });
      },
    });
  }

createNewNote(): void {
  this.dataService.createNote().subscribe(
    (resp) => {
      const id = resp?.data?._id;
      if (id) {
        this.router.navigateByUrl(`/edit/${encodeURIComponent(id)}`, {
          state: { filter: this.filter },
        });
      } else {
        console.warn('Create note did not return an _id', resp);
      }
    },
    (err: any) => {
      console.error('Error creating note:', err);
    }
  );
}
  openTemplateDialog(): void {
    this.templateDialogVisible = true;
    // Use cached templates for instant display
    this.templates = this.dataService.getTemplatesFromCache();
    this.templatesLoading = false;
    this.templateLoadError = '';
    this.updateTemplateMenuItems();
  }

  onTemplateMenuOpened(): void {
    // Use cached templates for instant display
    this.templates = this.dataService.getTemplatesFromCache();
    this.templatesLoading = false;
    this.templateLoadError = '';
    this.updateTemplateMenuItems();
  }

  loadTemplates(): void {
    // Legacy method - now just uses cache
    // Kept for backward compatibility
    this.templates = this.dataService.getTemplatesFromCache();
    this.templatesLoading = false;
    this.templateLoadError = '';
    this.updateTemplateMenuItems();
  }

  updateTemplateMenuItems(): void {
    this.templateMenuItems = TemplateLogic.buildTemplateMenuItems(
      this.templatesLoading,
      this.templateLoadError,
      this.templates,
      (template) => this.createNoteFromTemplate(template),
    );
  }
createNoteFromTemplate(template: Note): void {
  this.dataService.createNoteFromTemplate(template).subscribe(
    (resp) => {
      if (resp?.status === 'success') {
        const id = resp?.data?._id;
        if (id) {
          this.templateDialogVisible = false;
          this.router.navigateByUrl(`/edit/${encodeURIComponent(id)}`, {
            state: { filter: this.filter },
          });
          return;
        }
        console.warn('Create from template did not return an _id', resp);
      }
      const msg =
        (resp && 'error' in resp && resp.error?.message)
          ? resp.error.message
          : 'Failed to create note from template.';
      this.gDS.showGlobalDialog(
        'Create Note Failed',
        'Unable to create note from template',
        msg,
        DialogType.Error,
      );
    },
    (err: any) => {
      console.error('Error creating note from template:', err);
      const msg = err?.message || 'Failed to create note from template.';
      this.gDS.showGlobalDialog(
        'Create Note Failed',
        'Unable to create note from template',
        msg,
        DialogType.Error,
      );
    }
  );
}

  stripHtml(html: string): string {
    const tempElement = document.createElement('div');
    tempElement.innerHTML = html;
    return tempElement.textContent || tempElement.innerText || '';
  }

  getCategoryWithChildren(categoryID: string): Category[] {
    return FilterLogic.getCategoryWithChildren(categoryID, this.categories);
  }

  private deltaToPlainText(delta: Delta | null | undefined): string {
    return FilterLogic.deltaToPlainText(delta);
  }


  applyFilters(): void {
  if (this.isSemanticSearchActive) {
    this.runSemanticSearch();
    return;
  }

  this.applyKeywordStyleFilters();
}

  private applyKeywordStyleFilters(): void {
  this.semanticSearchLoading = false;
  this.semanticSearchError = '';
  this.semanticResults = [];

  const normalizedFilter = this.normalizeFilterForSearch(this.filter);

  this.filteredNotes = FilterLogic.applyFilters(
    this.allNotes,
    normalizedFilter,
    this.projects,
    this.categories,
    this.availableTags,
  );
}

  private runSemanticSearch(): void {
    const queryText = (this.filter.SearchString ?? '').trim();
    if (!queryText) {
      this.semanticSearchLoading = false;
      this.semanticSearchError = '';
      this.semanticResults = [];
      this.applyKeywordStyleFilters();
      return;
    }

    const topK = this.filter.SemanticTopK && this.filter.SemanticTopK > 0 ? this.filter.SemanticTopK : 10;
    const token = ++this.semanticSearchRequestToken;
    this.semanticSearchLoading = true;
    this.semanticSearchError = '';
    this.semanticResults = [];
    this.filteredNotes = [];

    this.semanticSearchService.searchNotes({ query_text: queryText, top_k: topK, prefer_recent: true }).subscribe({
      next: (response) => {
        if (token !== this.semanticSearchRequestToken) {
          return;
        }
        this.semanticResults = Array.isArray(response?.data?.results) ? response.data!.results! : [];
        this.filteredNotes = this.mapSemanticResultsToNotes(this.semanticResults, queryText);
        this.semanticSearchLoading = false;
      },
      error: (err) => {
        if (token !== this.semanticSearchRequestToken) {
          return;
        }
        this.semanticResults = [];
        this.semanticSearchLoading = false;
        this.semanticSearchError = err?.error?.message || err?.message || 'Semantic search failed.';
      },
    });
  }

  get isSemanticSearchActive(): boolean {
    return (this.filter.SearchType ?? '').trim().toLowerCase() === 'semantic';
  }

  private mapSemanticResultsToNotes(results: SemanticSearchResult[], queryText: string): Note[] {
    if (!Array.isArray(results) || results.length === 0) {
      return this.findLexicalSemanticFallbackNotes(queryText);
    }

    const notesById = new Map<string, Note>();
    for (const note of this.allNotes) {
      const rawId = String(note._id ?? '').trim();
      if (!rawId) {
        continue;
      }
      const normalized = this.normalizeSemanticNoteId(rawId);
      notesById.set(rawId, note);
      notesById.set(normalized, note);
      if (!rawId.startsWith('note:')) {
        notesById.set(`note:${rawId}`, note);
      }
      if (rawId.startsWith('note:')) {
        notesById.set(rawId.slice('note:'.length), note);
      }
    }

    const mapped: Note[] = [];
    for (const row of results) {
      const rawId = String(row.note_id ?? row.id ?? '').trim();
      if (!rawId) {
        continue;
      }
      const normalizedId = this.normalizeSemanticNoteId(rawId);
      const note = notesById.get(rawId) ?? notesById.get(normalizedId);
      if (note) {
        mapped.push(note);
      }
    }

    const seen = new Set(mapped.map((note) => String(note._id)));
    const lexicalFallback = this.findLexicalSemanticFallbackNotes(queryText).filter((note) => !seen.has(String(note._id)));
    return [...mapped, ...lexicalFallback];
  }

  private normalizeSemanticNoteId(value: string): string {
    const trimmed = String(value ?? '').trim().toLowerCase();
    return trimmed.startsWith('note:') ? trimmed.slice('note:'.length) : trimmed;
  }

  private findLexicalSemanticFallbackNotes(queryText: string): Note[] {
    const query = String(queryText ?? '').trim();
    if (!query) {
      return [];
    }

    // Use the same filter engine as normal search mode so lexical matches are consistent.
    const lexicalFilter = this.normalizeFilterForSearch({
      ...this.filter,
      SearchType: 'Title, Keywords & Tags',
      SearchString: query,
    });

    return FilterLogic.applyFilters(
      this.allNotes,
      lexicalFilter,
      this.projects,
      this.categories,
      this.availableTags,
    );
  }

  private normalizeFilterForSearch(input: FilterParams): FilterParams {
    return {
      ...input,
      TagIds: Array.isArray(input.TagIds) && input.TagIds.length > 0 ? input.TagIds : undefined,
      ProjectName: input.ProjectName && input.ProjectName.trim() !== '' ? input.ProjectName : undefined,
      Type: input.Type && input.Type.trim() !== '' ? input.Type : undefined,
      Status: input.Status && input.Status.trim() !== '' ? input.Status : undefined,
      SearchString: input.SearchString && input.SearchString.trim() !== '' ? input.SearchString : '',
    };
  }


  OLD__applyFilters(): void {
    this.filteredNotes = FilterLogic.applyFilters(
      this.allNotes,
      this.filter,
      this.projects,
      this.categories,
      this.availableTags,
    );
  }

  private async loadTagCatalog(): Promise<void> {
    try {
      const response = await firstValueFrom(this.dataService.getAllTags());
      if (response.status === 'success' && response.data) {
        this.availableTags = response.data;
      }
    } catch (err) {
      console.warn('Failed to load tags for search matching:', err);
    }
  }

  private async loadTagCatalogInBackground(): Promise<void> {
    await this.loadTagCatalog();
    this.cdr.markForCheck();
  }

  private async refreshUserSettingsInBackground(): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.userSettings.get().pipe(
          catchError((error) => {
            console.warn('Background user settings refresh failed', error);
            return of({ status: 'error' } as { status: 'error'; data?: undefined });
          }),
        ),
      );

      if (response.status === 'success' && response.data) {
        this.userSettings.setLocal(response.data);
      }
    } catch (err) {
      console.warn('Background user settings refresh exception', err);
    }
  }

  refreshApp() {
    this.refreshData();
  }

  private async refreshData(): Promise<void> {
    this.loadingCounter++;
    this.gDS.showGlobalDialog('Please wait', 'Refreshing data...', '', DialogType.Loading);
    try {
      await this.dataService.refreshLocalData();
      const response = await firstValueFrom(this.dataService.getAllNotesAndCategories());
      if (response.status === 'success' && response.data) {
        const { notes, categories } = response.data;
        this.allNotes = notes.sort(
          (a: Note, b: Note) =>
            new Date(b.created_on).getTime() - new Date(a.created_on).getTime(),
        );
        this.categories = categories;
        this.categoryItems = categories.map((c: Category) => ({
          ID: c._id,
          ItemName: c.Path,
          isSelected: false,
        }));
        this.assignSortOrderToProjects(this.projects, this.allNotes, this.card_sort);
        this.sortProjectsByCardSort();
        this.sortNotesByCardSort();
        this.applyFilters();
      } else {
        const msg = response.error?.message || 'Failed to refresh data.';
        this.alertService?.warn?.(msg, { life: 4000 });
      }
    } catch (err: any) {
      const msg = err?.message || 'Failed to refresh data.';
      this.alertService?.error?.(msg, { life: 5000 });
    } finally {
      this.loadingCounter--;
      this.gDS.hide();
      this.cdr.detectChanges();
    }
  }

  private async refreshFromMemory(): Promise<void> {
    try {
      const response = await firstValueFrom(this.dataService.getAllNotesAndCategories());
      if (response.status === 'success' && response.data) {
        const { notes, categories } = response.data;
        
        // Create a fingerprint of the data to detect actual changes
        const newFingerprint = this.createDataFingerprint(notes, categories);

        // Only update if data actually changed
        if (newFingerprint === HomeComponent.lastDataFingerprint) {
          console.log('[refreshFromMemory] Data unchanged, skipping redraw');
          return;
        }

        console.log('[refreshFromMemory] Data changed, updating view');
        HomeComponent.lastDataFingerprint = newFingerprint;

        this.allNotes = notes.sort(
          (a: Note, b: Note) =>
            new Date(b.created_on).getTime() - new Date(a.created_on).getTime(),
        );
        HomeComponent.cachedSortedNotes = this.allNotes;
        this.categories = categories;
        this.categoryItems = categories.map((c: Category) => ({
          ID: c._id,
          ItemName: c.Path,
          isSelected: false,
        }));
        this.assignSortOrderToProjects(this.projects, this.allNotes, this.card_sort);
        this.sortProjectsByCardSort();
        this.sortNotesByCardSort();
        this.applyFilters();
        this.cdr.detectChanges();
      }
    } catch (err) {
      console.error('Failed to refresh data from memory:', err);
    }
  }

  /**
   * Create a fingerprint of notes and categories to detect changes
   */
  private createDataFingerprint(notes: Note[], categories: Category[]): string {
    // Create a lightweight fingerprint using counts and key properties
    const noteFingerprint = notes.map(n => 
      `${n._id}:${n._rev}:${n.last_modified}`
    ).join('|');
    
    const categoryFingerprint = categories.map(c => 
      `${c._id}:${c.Path}`
    ).join('|');
    
    return `${notes.length}:${categories.length}:${noteFingerprint}:${categoryFingerprint}`;
  }


  // ##############################################################################
  // SELECT MODE / HANDLING BULK ACTIONS
  // ##############################################################################

  clearSelectedNotes() {
    this.selectedNoteIds = [];
  }

  selectAllFilteredNotes() {
    const uniqueIds = new Set(
      (this.filteredNotes || [])
        .map((note) => note?._id)
        .filter((id): id is string => typeof id === 'string' && id.trim().length > 0),
    );
    this.selectedNoteIds = Array.from(uniqueIds);
  }

  initializeBulkActions() {
    this.bulkActions = MenuStateLogic.initializeBulkActions();
    this.bulkActionMenuItems = MenuStateLogic.buildBulkActionMenuItems(
      this.bulkActions,
      (actionId) => this.onBulkActionSelected(actionId),
    );
  }

  selectMode() {
    console.log('Toggling select mode. Currently:', this.isSelectMode);
    const enabling = !this.isSelectMode;
    this.isSelectMode = enabling;
    this.cdr.detectChanges();
    UIStateLogic.updateBodySelectModeClass(enabling);
    
    const viewUpdate = UIStateLogic.getViewForSelectMode(
      enabling,
      this.selectedView,
      this.previousViewBeforeSelectMode,
    );
    this.selectedView = viewUpdate.view;
    this.previousViewBeforeSelectMode = viewUpdate.previousView;
    this.updateHomeTitle();

    if (!enabling) {
      this.selectedNoteIds = [];
    }
  }

  onBulkActionSelected(actionId: string) {
    if (actionId === 'set-favorite') {
      this.openFavoriteDialog();
      return;
    }
    const action = this.bulkActions.find((a) => a.id === actionId);
    if (!action) {
      return;
    }
    this.currentBulkAction = action;
    this.bulkDialogVisible = true;
  }

  onBulkActionDialogCancel() {
    this.bulkDialogVisible = false;
    this.currentBulkAction = null;
  }

  openFavoriteDialog(): void {
    this.favoriteDialogFavorite = true;
    this.favoriteDialogUpdateTimestamp = true;
    this.favoriteDialogVisible = true;
  }

  onFavoriteDialogCancel(): void {
    this.favoriteDialogVisible = false;
  }

  onFavoriteDialogApply(): void {
    this.favoriteDialogVisible = false;
  }

  get bulkRemovableTags(): Tag[] {
    if (!this.selectedNoteIds.length) {
      return [];
    }

    const selectedIdSet = new Set(this.selectedNoteIds);
    const usedTagIdSet = new Set<string>();

    for (const note of this.allNotes) {
      if (!selectedIdSet.has(note._id)) {
        continue;
      }
      for (const tagRef of note.Tags || []) {
        const tagId = typeof tagRef === 'string' ? tagRef : tagRef?._id;
        if (tagId) {
          usedTagIdSet.add(tagId);
        }
      }
    }

    if (!usedTagIdSet.size) {
      return [];
    }

    const byId = new Map((this.availableTags || []).map((tag) => [tag._id, tag]));

    return Array.from(usedTagIdSet)
      .map((id) => {
        const known = byId.get(id);
        if (known) {
          return known;
        }
        return {
          _id: id,
          type: 'tag',
          name: id,
          tagType: 'user',
          slug: id,
          parentTagId: null,
          isActive: true,
        } as Tag;
      })
      .sort((a, b) => String(a?.name ?? '').localeCompare(String(b?.name ?? ''), undefined, { sensitivity: 'base' }));
  }

  async onBulkActionDialogSubmit(request: BulkActionRequest) {
    console.log('Bulk action requested:', request, 'on notes', 
        this.selectedNoteIds);
    this.bulkDialogVisible = false;

    switch (request.action) {
      case 'export':
        await this.handleBulkExport(request.params);
        break;
      case 'print':
        await this.handleBulkPrintExport('print', request.params);
        break;
      case 'set-category':
        await this.handleBulkSetCategory(request.params?.categoryId,
            request.params?.updateTimestamp);
        break;
      case 'set-status':
        await this.handleBulkSetStatus(request.params?.status, 
            request.params?.updateTimestamp);
        break;
      case 'add-tags':
        await this.handleBulkAddTags(request.params?.tagIds);
        break;
      case 'remove-tags':
        await this.handleBulkRemoveTags(request.params?.removeAllTags, request.params?.removeTagIds);
        break;
      case 'delete':
        await this.handleBulkDelete();
        break;
      default:
        this.alertService.warn('This bulk action is not implemented yet.');
        break;
    }

    this.currentBulkAction = null;
  }

  
  private async handleBulkSetCategory(categoryId?: string, updateTimestamp?: boolean) {
    if (!this.isSelectMode) {
      this.gDS.showGlobalDialog(
        'Bulk Action Unavailable',
        'Select Mode needs to be enabled to run bulk actions.',
        '',
        DialogType.Error,
      );
      return;
    }

    if (!this.selectedNoteIds.length) {
      this.gDS.showGlobalDialog(
        'No Notes Selected',
        'Select one or more notes before applying a bulk category.',
        '',
        DialogType.Error,
      );
      return;
    }

    if (!categoryId) {
      this.gDS.showGlobalDialog(
        'Category Required',
        'Choose a category before applying the bulk action.',
        '',
        DialogType.Error,
      );
      return;
    }

    const shouldUpdateTimestamp = updateTimestamp !== false;

    try {
      const resp = await firstValueFrom(
        this.dataService.bulkSetCategory(this.selectedNoteIds, categoryId,
            shouldUpdateTimestamp));

      if (resp.status === 'success') {
        const updatedCount = this.getUpdatedCountFromBulk(resp.data, 
            this.selectedNoteIds.length);
        const message = resp.data?.message || 
            `Category set for ${updatedCount} note(s).`;
        this.alertService.success(message);
        this.applyBulkCategoryLocally(categoryId, shouldUpdateTimestamp);
        this.clearSelectedNotes();
        return;
      }

      const errorMsg = resp.error?.message || 'Bulk category update failed.';
      this.gDS.showGlobalDialog('Bulk Set Category Failed', 
        'Unable to update categories.', errorMsg, DialogType.Error);
    } catch (err: any) {
      const errorMsg = err?.message || 'Unexpected error while updating categories.';
      this.gDS.showGlobalDialog('Bulk Set Category Failed', 
        'Unable to update categories.', errorMsg, DialogType.Error);
    }
  }

  private async handleBulkSetStatus(status?: string, updateTimestamp?: boolean) {
    if (!this.isSelectMode) {
      this.gDS.showGlobalDialog(
        'Bulk Action Unavailable',
        'Select Mode needs to be enabled to run bulk actions.',
        '',
        DialogType.Error,
      );
      return;
    }

    if (!this.selectedNoteIds.length) {
      this.gDS.showGlobalDialog(
        'No Notes Selected',
        'Select one or more notes before applying a bulk status.',
        '',
        DialogType.Error,
      );
      return;
    }

    if (!status) {
      this.gDS.showGlobalDialog(
        'Status Required',
        'Choose a status before applying the bulk action.',
        '',
        DialogType.Error,
      );
      return;
    }

    const shouldUpdateTimestamp = updateTimestamp !== false;

    try {
      const resp = await firstValueFrom(
        this.dataService.bulkSetStatus(this.selectedNoteIds, status, 
            shouldUpdateTimestamp),
      );

      if (resp.status === 'success') {
        const updatedCount = this.getUpdatedCountFromBulk(resp.data, 
            this.selectedNoteIds.length);
        const message = resp.data?.message || 
            `Status set to "${status}" for ${updatedCount} note(s).`;
        this.alertService.success(message);
        this.applyBulkStatusLocally(status, shouldUpdateTimestamp);
        this.clearSelectedNotes();
        return;
      }

      const errorMsg = resp.error?.message || 'Bulk status update failed.';
      this.gDS.showGlobalDialog('Bulk Set Status Failed', 
        'Unable to update status.', errorMsg, DialogType.Error);
    } catch (err: any) {
      const errorMsg = err?.message || 'Unexpected error while updating status.';
      this.gDS.showGlobalDialog('Bulk Set Status Failed', 
        'Unable to update status.', errorMsg, DialogType.Error);
    }
  }

  private async handleBulkDelete() {
    if (!this.isSelectMode) {
      this.gDS.showGlobalDialog(
        'Bulk Action Unavailable',
        'Select Mode needs to be enabled to run bulk actions.',
        '',
        DialogType.Error,
      );
      return;
    }

    if (!this.selectedNoteIds.length) {
      this.gDS.showGlobalDialog('No Notes Selected', 
        'Select one or more notes before deleting.', '', DialogType.Error);
      return;
    }

    try {
      const resp = await 
        firstValueFrom(this.dataService.bulkDelete(this.selectedNoteIds));

      if (resp.status === 'success') {
        const deletedCount = 
            this.getUpdatedCountFromBulk(resp.data, this.selectedNoteIds.length);
        const message = resp.data?.message || `Deleted ${deletedCount} note(s).`;
        this.alertService.success(message);
        this.removeNotesLocally(this.selectedNoteIds);
        this.clearSelectedNotes();
        // Refresh template cache in case templates were deleted
        this.dataService.refreshTemplateCache();
        return;
      }

      const errorMsg = resp.error?.message || 'Bulk delete failed.';
      this.gDS.showGlobalDialog('Bulk Delete Failed', 
        'Unable to delete notes.', errorMsg, DialogType.Error);
    } catch (err: any) {
      const errorMsg = err?.message || 'Unexpected error while deleting notes.';
      this.gDS.showGlobalDialog('Bulk Delete Failed', 
        'Unable to delete notes.', errorMsg, DialogType.Error);
    }
  }

  private async handleBulkAddTags(tagIds?: string[]) {
    if (!this.isSelectMode) {
      this.gDS.showGlobalDialog(
        'Bulk Action Unavailable',
        'Select Mode needs to be enabled to run bulk actions.',
        '',
        DialogType.Error,
      );
      return;
    }

    if (!this.selectedNoteIds.length) {
      this.gDS.showGlobalDialog(
        'No Notes Selected',
        'Select one or more notes before adding tags.',
        '',
        DialogType.Error,
      );
      return;
    }

    const normalizedTagIds = Array.from(new Set((tagIds || []).filter((id) => !!id)));
    if (!normalizedTagIds.length) {
      this.gDS.showGlobalDialog(
        'Tags Required',
        'Choose one or more tags before applying the bulk action.',
        '',
        DialogType.Error,
      );
      return;
    }

    try {
      const resp = await firstValueFrom(this.dataService.bulkAddTags(this.selectedNoteIds, normalizedTagIds));

      if (resp.status === 'success') {
        const updatedCount = this.getUpdatedCountFromBulk(resp.data, this.selectedNoteIds.length);
        const message = resp.data?.message || `Added tags to ${updatedCount} note(s).`;
        this.alertService.success(message);

        const failedIds = resp.data?.failedIds ?? resp.data?.failed ?? [];
        const failedSet = new Set(failedIds);
        const successfulNoteIds = this.selectedNoteIds.filter((noteId) => !failedSet.has(noteId));
        if (failedIds.length > 0) {
          this.alertService.warn(`Tag add completed with warnings: ${failedIds.length} note(s) failed.`);
        }

        this.applyBulkTagsLocally(normalizedTagIds, successfulNoteIds);
        this.clearSelectedNotes();
        return;
      }

      if (resp.status === 'warning') {
        const warningMsg = resp.error?.message || resp.data?.message || 'Bulk add tags completed with warnings.';
        this.alertService.warn(warningMsg);
        const failedIds = resp.data?.failedIds ?? resp.data?.failed ?? [];
        const failedSet = new Set(failedIds);
        const successfulNoteIds = this.selectedNoteIds.filter((noteId) => !failedSet.has(noteId));
        this.applyBulkTagsLocally(normalizedTagIds, successfulNoteIds);
        this.clearSelectedNotes();
        return;
      }

      const errorMsg = resp.error?.message || 'Bulk add tags failed.';
      this.gDS.showGlobalDialog('Bulk Add Tags Failed', 'Unable to add tags to notes.', errorMsg, DialogType.Error);
    } catch (err: any) {
      const errorMsg = err?.message || 'Unexpected error while adding tags.';
      this.gDS.showGlobalDialog('Bulk Add Tags Failed', 'Unable to add tags to notes.', errorMsg, DialogType.Error);
    }
  }

  private async handleBulkRemoveTags(removeAllTags?: boolean, removeTagIds?: string[]) {
    if (!this.isSelectMode) {
      this.gDS.showGlobalDialog(
        'Bulk Action Unavailable',
        'Select Mode needs to be enabled to run bulk actions.',
        '',
        DialogType.Error,
      );
      return;
    }

    if (!this.selectedNoteIds.length) {
      this.gDS.showGlobalDialog(
        'No Notes Selected',
        'Select one or more notes before removing tags.',
        '',
        DialogType.Error,
      );
      return;
    }

    const removeAll = removeAllTags !== false;
    const normalizedTagIds = Array.from(new Set((removeTagIds || []).filter((id) => !!id)));

    if (!removeAll && !normalizedTagIds.length) {
      this.gDS.showGlobalDialog(
        'Tags Required',
        'Choose one or more tags before applying the bulk remove action.',
        '',
        DialogType.Error,
      );
      return;
    }

    try {
      const resp = await firstValueFrom(this.dataService.bulkRemoveTags(this.selectedNoteIds, normalizedTagIds, removeAll));

      if (resp.status === 'success') {
        const updatedCount = this.getUpdatedCountFromBulk(resp.data, this.selectedNoteIds.length);
        const message = resp.data?.message || (removeAll
          ? `Removed all tags from ${updatedCount} note(s).`
          : `Removed selected tags from ${updatedCount} note(s).`);
        this.alertService.success(message);

        const failedIds = resp.data?.failedIds ?? resp.data?.failed ?? [];
        const failedSet = new Set(failedIds);
        const successfulNoteIds = this.selectedNoteIds.filter((noteId) => !failedSet.has(noteId));
        if (failedIds.length > 0) {
          this.alertService.warn(`Tag removal completed with warnings: ${failedIds.length} note(s) failed.`);
        }

        this.applyBulkRemoveTagsLocally(removeAll, normalizedTagIds, successfulNoteIds);
        this.clearSelectedNotes();
        return;
      }

      if (resp.status === 'warning') {
        const warningMsg = resp.error?.message || resp.data?.message || 'Bulk remove tags completed with warnings.';
        this.alertService.warn(warningMsg);
        const failedIds = resp.data?.failedIds ?? resp.data?.failed ?? [];
        const failedSet = new Set(failedIds);
        const successfulNoteIds = this.selectedNoteIds.filter((noteId) => !failedSet.has(noteId));
        this.applyBulkRemoveTagsLocally(removeAll, normalizedTagIds, successfulNoteIds);
        this.clearSelectedNotes();
        return;
      }

      const errorMsg = resp.error?.message || 'Bulk remove tags failed.';
      this.gDS.showGlobalDialog('Bulk Remove Tags Failed', 'Unable to remove tags from notes.', errorMsg, DialogType.Error);
    } catch (err: any) {
      const errorMsg = err?.message || 'Unexpected error while removing tags.';
      this.gDS.showGlobalDialog('Bulk Remove Tags Failed', 'Unable to remove tags from notes.', errorMsg, DialogType.Error);
    }
  }

  private async handleBulkPrintExport(mode: 'print', params?: BulkActionRequest['params']) {
    if (!this.isSelectMode) {
      this.gDS.showGlobalDialog(
        'Bulk Action Unavailable',
        'Select Mode needs to be enabled to run bulk actions.',
        '',
        DialogType.Error,
      );
      return;
    }

    if (!this.selectedNoteIds.length) {
      this.gDS.showGlobalDialog(
        'No Notes Selected',
        'Select one or more notes before exporting.',
        '',
        DialogType.Error,
      );
      return;
    }

    try {
      await this.notePrintExportService.printOrExportNotesByIds(
            this.selectedNoteIds, this.allNotes, mode, {
        fileName: params?.fileName,
        includeTableOfContents: params?.includeTableOfContents,
        pageBreakBetweenNotes: params?.pageBreakBetweenNotes,
        pageNumbers: params?.pageNumbers,
      });
      this.alertService.success('Print job created for selected notes.');
      this.clearSelectedNotes();
    } catch (err: any) {
      const message = err?.message || 'Failed to generate the PDF export.';
      this.gDS.showGlobalDialog('Bulk Export Failed',
        'Unable to export selected notes.', message, DialogType.Error);
    }
  }

  /** Reads the List view's persisted column-sort (sortBy/sortOrder) so the
   * export dialog's "Default (as current view)" option can replicate it,
   * regardless of which Home view is actually active right now — List is the
   * only view with an explicit, user-controlled sort order to mirror. */
  private getCurrentListSortState(): { sortBy: string; sortOrder: 'asc' | 'desc' } {
    try {
      const raw = localStorage.getItem(LIST_SORT_STATE_KEY);
      if (!raw) return LIST_SORT_STATE_DEFAULT;
      const parsed = JSON.parse(raw);
      if (typeof parsed?.sortBy === 'string' && (parsed?.sortOrder === 'asc' || parsed?.sortOrder === 'desc')) {
        return { sortBy: parsed.sortBy, sortOrder: parsed.sortOrder };
      }
    } catch {
      // Ignore malformed/unavailable storage; fall back to the default below.
    }
    return LIST_SORT_STATE_DEFAULT;
  }

  private async handleBulkExport(params?: BulkActionRequest['params']) {
    if (!this.isSelectMode) {
      this.gDS.showGlobalDialog(
        'Bulk Action Unavailable',
        'Select Mode needs to be enabled to run bulk actions.',
        '',
        DialogType.Error,
      );
      return;
    }

    if (!this.selectedNoteIds.length) {
      this.gDS.showGlobalDialog(
        'No Notes Selected',
        'Select one or more notes before exporting.',
        '',
        DialogType.Error,
      );
      return;
    }

    const format: ExportFormat = params?.exportFormat || 'pdf';
    const sortOrder: ExportSortOrder = params?.sortOrder || 'default';

    try {
      const orderedIds = BulkActionsLogic.sortNoteIdsForExport(
        this.selectedNoteIds,
        this.allNotes,
        sortOrder,
        this.availableTags,
        this.getCurrentListSortState(),
      );
      const fileName = this.notePrintExportService.sanitizeFilenameForExtension(
        BulkActionsLogic.buildTimestampedExportBaseName(params?.fileName),
        `.${format}`,
      );

      if (format === 'pdf') {
        await this.notePrintExportService.printOrExportNotesByIds(orderedIds, this.allNotes, 'export', {
          fileName,
          includeTableOfContents: params?.includeTableOfContents,
          pageBreakBetweenNotes: params?.pageBreakBetweenNotes,
          pageNumbers: params?.pageNumbers,
        });
      } else if (format === 'docx') {
        await this.notePrintExportService.exportNotesToDocxByIds(orderedIds, this.allNotes, {
          fileName,
          includeTableOfContents: params?.includeTableOfContents,
          pageBreakBetweenNotes: params?.pageBreakBetweenNotes,
          pageNumbers: params?.pageNumbers,
          includeMetaData: params?.includeMetaData,
        });
      } else {
        const orderedNotes = orderedIds
          .map((id) => this.allNotes.find((note) => note._id === id))
          .filter((note): note is Note => !!note);
        const jsonStr = JSON.stringify(orderedNotes, null, 2);
        const blob = new Blob([jsonStr], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }

      this.alertService.success(`Exported selected notes to ${format.toUpperCase()}.`);
      this.clearSelectedNotes();
    } catch (err: any) {
      const message = err?.message || `Failed to generate the ${format.toUpperCase()} export.`;
      this.gDS.showGlobalDialog('Bulk Export Failed', 'Unable to export selected notes.', message, DialogType.Error);
    }
  }

  private applyBulkCategoryLocally(categoryId: string, updateTimestamp: boolean) {
    this.allNotes = BulkActionsLogic.applyBulkCategoryLocally(
      this.allNotes,
      this.selectedNoteIds,
      categoryId,
      updateTimestamp,
    );
    this.applyFilters();
  }

  private applyBulkStatusLocally(status: string, updateTimestamp: boolean) {
    this.allNotes = BulkActionsLogic.applyBulkStatusLocally(
      this.allNotes,
      this.selectedNoteIds,
      status,
      updateTimestamp,
    );
    this.applyFilters();
  }

  private removeNotesLocally(noteIds: string[]) {
    this.allNotes = BulkActionsLogic.removeNotesLocally(this.allNotes, noteIds);
    this.applyFilters();
  }

  private applyBulkTagsLocally(tagIds: string[], noteIds: string[] = this.selectedNoteIds) {
    this.allNotes = BulkActionsLogic.applyBulkTagsLocally(
      this.allNotes,
      noteIds,
      tagIds,
    );
    this.applyFilters();
  }

  private applyBulkRemoveTagsLocally(removeAll: boolean, tagIds: string[], noteIds: string[] = this.selectedNoteIds) {
    this.allNotes = BulkActionsLogic.applyBulkRemoveTagsLocally(
      this.allNotes,
      noteIds,
      tagIds,
      removeAll,
    );
    this.applyFilters();
  }

  private getUpdatedCountFromBulk(
    data: BulkCategoryResult | BulkStatusResult | BulkDeleteResult | BulkTagAddResult | BulkTagRemoveResult | undefined,
    fallback: number,
  ): number {
    return BulkActionsLogic.getUpdatedCountFromBulk(data, fallback);
  }

  toggleZoomPanel(event: MouseEvent): void {
    event.stopPropagation();
    this.showZoomPanel = !this.showZoomPanel;
  }

  onSystemZoomChange(event: any): void {
    console.log('System zoom factor changed to', this.systemZoomFactor);
    this.userSettings.setLocal({ systemZoomFactor: this.systemZoomFactor });
    this.homeZoomScale = this.systemZoomFactor / 100;
  }

  persistSystemZoom(): void {
    this.userSettings.setLocal({ systemZoomFactor: this.systemZoomFactor });
  }
}
